import React from 'react';
import { Box, Typography } from '@mui/material';
import { BRAND_COLORS, FONTS } from '../types/index.ts';
import { fillBarColor } from './fillColors.ts';

interface DensityRailProps {
  values: number[]; // utilization % per candidate, in ranked order
  bestIndex?: number;
  maxColumns?: number;
}

// Ranks every candidate as an equal-width mini fill-column so relative fullness
// (the winner) reads as a single height comparison before any number. Rail =
// cross-candidate comparison only — deliberately NO numerals (that's the gauge's job).
const DensityRail: React.FC<DensityRailProps> = ({ values, bestIndex = 0, maxColumns = 40 }) => {
  if (!values || values.length < 2) return null;
  const shown = values.slice(0, maxColumns);
  const hidden = values.length - shown.length;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-end',
        gap: '5px',
        height: 54,
        px: 1.5,
        pt: 2,
        pb: 1,
        mb: 2.5,
        backgroundColor: BRAND_COLORS.cardStock,
        border: `1.5px solid ${BRAND_COLORS.ink}`,
        borderRadius: '6px',
      }}
    >
      <Typography
        component="span"
        sx={{
          position: 'absolute',
          top: -9,
          left: 12,
          px: 0.75,
          backgroundColor: BRAND_COLORS.cardStock,
          fontFamily: FONTS.display,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontSize: 10,
          color: BRAND_COLORS.slateText,
        }}
      >
        Density · all candidates
      </Typography>

      {shown.map((v, i) => {
        const u = Math.max(0, Math.min(100, v));
        const isBest = i === bestIndex;
        return (
          <Box
            key={i}
            title={`${u.toFixed(1)}%`}
            sx={{
              flex: '1 1 0',
              maxWidth: 30,
              minWidth: 6,
              height: '100%',
              display: 'flex',
              alignItems: 'flex-end',
              backgroundColor: BRAND_COLORS.slateSoft,
              borderRadius: '2px 2px 0 0',
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: `${u}%`,
                backgroundColor: fillBarColor(u),
                borderRadius: '2px 2px 0 0',
                outline: isBest ? `1.5px solid ${BRAND_COLORS.ink}` : 'none',
                outlineOffset: '-1.5px',
                transformOrigin: 'bottom',
                animation: 'ld-rise-y 500ms cubic-bezier(0.22,1,0.36,1) both',
              }}
            />
          </Box>
        );
      })}

      {hidden > 0 && (
        <Typography
          component="span"
          sx={{ alignSelf: 'center', ml: 1, fontFamily: FONTS.mono, fontSize: 11, color: BRAND_COLORS.slateText }}
        >
          +{hidden}
        </Typography>
      )}
    </Box>
  );
};

export default DensityRail;
